import React from 'react';
import { Box, Typography, Autocomplete, TextField, Chip, Alert } from '@mui/material';
import { AccountTree as RelatedIcon } from '@mui/icons-material';

import { ImpactAiPreset } from '../types';

interface RelatedPresetsSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  availablePresets: ImpactAiPreset[];
  currentPresetId?: string;
}

const RelatedPresetsSelector: React.FC<RelatedPresetsSelectorProps> = ({
  value,
  onChange,
  availablePresets,
  currentPresetId,
}) => {
  // Filter out the current preset from available options
  const selectablePresets = availablePresets.filter((preset) => preset.id !== currentPresetId);

  const selectedPresets = selectablePresets.filter((preset) => value.includes(preset.id));

  const handleChange = (_event: React.SyntheticEvent, newValue: ImpactAiPreset[]) => {
    onChange(newValue.map((preset) => preset.id));
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Alert severity="info" icon={<RelatedIcon />}>
        <Typography variant="body2">
          <strong>Related Presets</strong> are other presets that should be included when this
          preset is loaded. This ensures that subPreset actions can reference these presets
          dynamically.
        </Typography>
      </Alert>

      <Autocomplete
        multiple
        value={selectedPresets}
        onChange={handleChange}
        options={selectablePresets}
        getOptionLabel={(preset) => preset.title}
        renderOption={(props, preset) => (
          <Box component="li" {...props}>
            <Box display="flex" flexDirection="column" width="100%">
              <Typography variant="body2" fontWeight={500}>
                {preset.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {preset.description} • ID: {preset.id}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Related Presets"
            placeholder="Select presets that should be included..."
            helperText="Choose presets that this preset may reference through subPreset actions"
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((preset, index) => {
            const { key, ...rest } = getTagProps({ index });
            return <Chip key={preset.id} label={preset.title} size="small" {...rest} />;
          })
        }
      />

      {value.length > 0 && (
        <Alert severity="success">
          <Typography variant="body2">
            <strong>Selected:</strong> {value.length} related preset{value.length === 1 ? '' : 's'}{' '}
            will be included when this preset is loaded.
          </Typography>
        </Alert>
      )}

      <Alert severity="warning">
        <Typography variant="body2">
          <strong>Note:</strong> Only add presets that are actually referenced by subPreset actions
          to avoid unnecessarily large query responses.
        </Typography>
      </Alert>
    </Box>
  );
};

export default RelatedPresetsSelector;
